import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  ArrowLeft, Trash2, Send, TrendingUp, TrendingDown, ExternalLink,
  Bell, Newspaper, Loader2, BarChart3,
} from 'lucide-react';
import MarkdownMessage from '../../../components/ui/markdown-message';
import { usePortfolioStore } from '../../../state/portfolioStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUICK_QUESTIONS = [
  'Should I buy more?',
  'What is the risk?',
  'Recent performance analysis',
  'Sector outlook',
  'Key financials summary',
];

export default function StockDetailPage() {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const overview = usePortfolioStore((s) => s.overview);
  const fetchOverview = usePortfolioStore((s) => s.fetchOverview);
  const holdings = usePortfolioStore((s) => s.holdings);
  const fetchHoldings = usePortfolioStore((s) => s.fetchHoldings);
  const deleteHolding = usePortfolioStore((s) => s.deleteHolding);

  const [question, setQuestion] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [stockNews, setStockNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);

  useEffect(() => {
    if (!holdings.length) fetchHoldings();
    if (!overview) fetchOverview();
  }, [fetchHoldings, fetchOverview, holdings.length, overview]);

  const allHoldings = overview?.holdings || [];
  const holding = allHoldings.find((h) => h.symbol === symbol) || holdings.find((h) => h.symbol === symbol);

  useEffect(() => {
    if (!symbol) return;
    const yf_sym = `${symbol}.NS`;

    setChartLoading(true);
    axios.get(`${API}/chart-data/${yf_sym}?period=3mo`)
      .then(({ data }) => {
        const points = (data.data || data || []).map((d) => ({
          date: d.date,
          close: d.close,
        }));
        setChartData(points);
      })
      .catch(() => {})
      .finally(() => setChartLoading(false));

    setNewsLoading(true);
    axios.get(`${API}/news/${yf_sym}?limit=5`)
      .then(({ data }) => setStockNews(data?.data?.news || []))
      .catch(() => {})
      .finally(() => setNewsLoading(false));
  }, [symbol]);

  const askQuestion = async (q) => {
    const text = q || question;
    if (!text.trim() || !holding) return;

    setChatHistory((prev) => [...prev, { role: 'user', content: text }]);
    setQuestion('');
    setAiLoading(true);

    try {
      const cp = holding.currentPrice || holding.avg_buy_price || 0;
      const response = await axios.post(`${API}/chat`, {
        message: `About ${holding.company_name || holding.companyName || symbol}: ${text}`,
        context: `Stock: ${symbol}, Qty: ${holding.quantity}, Avg: ${holding.avg_buy_price || holding.avgBuyPrice}, Current: ${cp}, Sector: ${holding.sector || 'Unknown'}`,
      });
      setChatHistory((prev) => [...prev, { role: 'assistant', content: response.data.response || response.data.message }]);
    } catch {
      setChatHistory((prev) => [...prev, { role: 'assistant', content: 'Unable to get AI analysis at this time.' }]);
    } finally {
      setAiLoading(false);
    }
  };

  const handleDelete = () => {
    if (window.confirm(`Remove ${holding?.company_name || holding?.companyName || symbol} from your portfolio?`)) {
      deleteHolding(holding.id);
      navigate('/public/portfolio');
    }
  };

  if (!holding) {
    return (
      <div className="max-w-4xl mx-auto text-center py-16">
        <p className="text-lg text-muted-foreground">Holding not found for {symbol}</p>
        <Button onClick={() => navigate('/public/portfolio')} className="mt-4">Back to Portfolio</Button>
      </div>
    );
  }

  const cp = holding.currentPrice || holding.avg_buy_price || holding.avgBuyPrice || 0;
  const avg = holding.avg_buy_price || holding.avgBuyPrice || 0;
  const qty = holding.quantity || 0;
  const invested = avg * qty;
  const currentVal = cp * qty;
  const pnl = currentVal - invested;
  const pnlPct = invested > 0 ? (pnl / invested) * 100 : 0;
  const dayPct = holding.dayChangePercent || 0;
  const weight = holding.weight || 0;
  const companyName = holding.company_name || holding.companyName || symbol;

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">{companyName}</h1>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{symbol} · {holding.exchange || 'NSE'}</span>
              {holding.sector && <Badge variant="secondary" className="text-[10px]">{holding.sector}</Badge>}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={() => navigate('/public/portfolio/alerts')}>
            <Bell className="w-3.5 h-3.5" /> Set Alert
          </Button>
          <Button variant="outline" size="sm" onClick={handleDelete} className="text-red-500 hover:text-red-700 gap-1.5 text-xs">
            <Trash2 className="w-3.5 h-3.5" /> Remove
          </Button>
        </div>
      </div>

      {/* Price Chart */}
      <Card className="border-border/50">
        <CardHeader className="pb-1">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-2xl font-bold">₹{cp.toFixed(2)}</span>
              <span className={`ml-2 text-sm font-medium ${dayPct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {dayPct >= 0 ? <TrendingUp className="w-4 h-4 inline" /> : <TrendingDown className="w-4 h-4 inline" />}
                {' '}{dayPct >= 0 ? '+' : ''}{dayPct.toFixed(2)}% today
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {chartLoading ? (
            <div className="h-48 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : chartData.length > 1 ? (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="stockGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={pnl >= 0 ? '#10b981' : '#ef4444'} stopOpacity={0.15} />
                      <stop offset="95%" stopColor={pnl >= 0 ? '#10b981' : '#ef4444'} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" />
                  <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} width={55} domain={['auto', 'auto']} />
                  <Tooltip formatter={(v) => [`₹${Number(v).toFixed(2)}`, 'Price']} />
                  <Area type="monotone" dataKey="close" stroke={pnl >= 0 ? '#10b981' : '#ef4444'} fill="url(#stockGrad)" strokeWidth={2} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-sm text-muted-foreground">Chart data unavailable</div>
          )}
          {/* Buy price reference line note */}
          {chartData.length > 0 && (
            <p className="text-[10px] text-muted-foreground mt-1">Avg buy price: ₹{avg.toFixed(2)}</p>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left column */}
        <div className="space-y-4">
          {/* Position */}
          <Card className="border-border/50">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Your Position</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              {[
                ['Quantity', qty],
                ['Avg. Buy Price', `₹${avg.toFixed(2)}`],
                ['Invested Value', `₹${invested.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`],
                ['Current Value', `₹${currentVal.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between border-b border-border/30 pb-1">
                  <span className="text-muted-foreground">{label}</span>
                  <span className="font-medium">{value}</span>
                </div>
              ))}
              <div className="flex justify-between pt-1">
                <span className="text-muted-foreground font-semibold">P&L</span>
                <span className={`font-bold ${pnl >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {pnl >= 0 ? '+' : ''}₹{pnl.toFixed(0)} ({pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%)
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Fundamentals */}
          {(holding.pe || holding.pb || holding.high52Week) && (
            <Card className="border-border/50">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Key Metrics</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                {[
                  ['P/E Ratio', holding.pe ? holding.pe.toFixed(1) : '—'],
                  ['P/B Ratio', holding.pb ? holding.pb.toFixed(2) : '—'],
                  ['52W High', holding.high52Week ? `₹${holding.high52Week.toFixed(2)}` : '—'],
                  ['52W Low', holding.low52Week ? `₹${holding.low52Week.toFixed(2)}` : '—'],
                  ['Market Cap', holding.marketCapCategory || '—'],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between border-b border-border/30 pb-1">
                    <span className="text-muted-foreground">{label}</span>
                    <span className="font-medium">{value}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Weight */}
          <Card className="border-border/50">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground mb-1">Portfolio Weight</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(weight, 100)}%` }} />
                </div>
                <span className="text-sm font-semibold">{weight.toFixed(1)}%</span>
              </div>
            </CardContent>
          </Card>

          {/* News */}
          <Card className="border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-1.5">
                <Newspaper className="w-3.5 h-3.5" /> Recent News
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {newsLoading ? (
                <div className="py-6 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-muted-foreground" /></div>
              ) : stockNews.length > 0 ? (
                <div className="divide-y divide-border/30">
                  {stockNews.slice(0, 5).map((n, i) => (
                    <a key={i} href={n.url} target="_blank" rel="noopener noreferrer" className="block px-4 py-2.5 hover:bg-secondary/50 transition-colors">
                      <p className="text-xs font-medium line-clamp-2">{n.title}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{n.source} {n.date ? `· ${n.date}` : ''}</p>
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground py-6 text-center">No recent news</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right column - AI Analysis */}
        <div className="lg:col-span-2">
          <Card className="h-full flex flex-col border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">AI Analysis</CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
              {chatHistory.length === 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {QUICK_QUESTIONS.map((q) => (
                    <button key={q} onClick={() => askQuestion(q)} className="px-3 py-1.5 text-xs rounded-full border border-border hover:bg-emerald-50 dark:hover:bg-emerald-950 transition-colors">
                      {q}
                    </button>
                  ))}
                </div>
              )}

              <div className="flex-1 overflow-y-auto space-y-3 mb-4 max-h-[500px]">
                {chatHistory.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] px-4 py-2.5 rounded-xl ${
                      msg.role === 'user'
                        ? 'bg-emerald-600 text-white rounded-br-sm text-sm'
                        : 'bg-secondary rounded-bl-sm'
                    }`}>
                      {msg.role === 'user' ? msg.content : <MarkdownMessage content={msg.content} />}
                    </div>
                  </div>
                ))}
                {aiLoading && (
                  <div className="flex justify-start">
                    <div className="bg-secondary px-4 py-3 rounded-xl rounded-bl-sm">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" />
                        <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Input placeholder="Ask about this stock..." value={question} onChange={(e) => setQuestion(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && askQuestion()} />
                <Button onClick={() => askQuestion()} disabled={!question.trim() || aiLoading} className="bg-emerald-600 hover:bg-emerald-700">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
